import {
  GoogleGenAI,
  Modality,
  FunctionDeclaration,
  Type,
} from "@google/genai";

import { DJPersona, Station } from "../types";

const getAI = () => {
  const apiKey = process.env.API_KEY;
  if (!apiKey) throw new Error("API_KEY not found");
  return new GoogleGenAI({ apiKey });
};

const tuneStationTool: FunctionDeclaration = {
  name: "tuneStation",
  parameters: {
    type: Type.OBJECT,
    description:
      "Tune into or recommend a specific type of radio station based on genre, country, or name.",
    properties: {
      genre: {
        type: Type.STRING,
        description: "Music genre (e.g., jazz, rock, news, pop)",
      },
      country: {
        type: Type.STRING,
        description: "Country name (e.g., France, Japan, USA)",
      },
      mood: {
        type: Type.STRING,
        description: "Mood (e.g., chill, party, upbeat)",
      },
    },
    required: [],
  },
};

export const generateSpeech = async (
  text: string,
  persona: DJPersona = "upbeat",
): Promise<ArrayBuffer | null> => {
  const ai = getAI();

  let voiceName = "Fenrir";
  if (persona === "chill") voiceName = "Kore";
  if (persona === "knowledgeable") voiceName = "Charon";

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text: text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName },
          },
        },
      },
    });

    const base64Audio =
      response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (base64Audio) {
      const binaryString = atob(base64Audio);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    }
    return null;
  } catch (error) {
    console.error("TTS Error:", error);
    return null;
  }
};

export const searchRadioInfo = async (query: string) => {
  const ai = getAI();
  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: `Find detailed information about: ${query}. Keep it brief and suitable for a radio readout.`,
      config: {
        tools: [{ googleSearch: {} }],
      },
    });

    const text = response.text || "I couldn't find that information right now.";
    const groundingChunks =
      response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];

    const sources = groundingChunks
      .map((chunk: { web?: { title: string; uri: string } }) =>
        chunk.web?.uri ? { title: chunk.web.title, url: chunk.web.uri } : null,
      )
      .filter((item: unknown) => item !== null);

    return { text, sources };
  } catch (error) {
    console.error("Search Error:", error);
    return {
      text: "Sorry, I'm having trouble accessing the global database.",
      sources: [],
    };
  }
};

export const chatWithDJ = async (message: string, persona: DJPersona) => {
  const ai = getAI();

  let instructions = "You are DJ Eater, a world-famous AI Radio DJ. ";
  if (persona === "upbeat")
    instructions +=
      "Be energetic, fast-paced, and use slang. Hype up the music.";
  if (persona === "chill")
    instructions += "Be calm, relaxed, smooth, and zen. Keep vibes low-key.";
  if (persona === "knowledgeable")
    instructions +=
      "Be intellectual, factual, and precise. Focus on history and details.";

  instructions +=
    " If the user asks to play music, change the station, or for a recommendation, YOU MUST use the 'tuneStation' tool.";

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: message,
      config: {
        systemInstruction: instructions,
        tools: [{ functionDeclarations: [tuneStationTool] }],
      },
    });

    const toolCall = response.functionCalls?.[0];
    const text =
      response.text ||
      (toolCall ? "You got it! Tuning in now..." : "Stay tuned!");

    return { text, toolCall };
  } catch (error) {
    console.error("Chat Error:", error);
    return { text: "Technical difficulties on the line!", toolCall: null };
  }
};

export const generateStationIntro = async (
  station: Station,
  persona: DJPersona,
): Promise<string> => {
  const ai = getAI();

  let tone = "energetic and cool";
  if (persona === "chill") tone = "calm and soothing";
  if (persona === "knowledgeable") tone = "professional and informative";

  const prompt = `
      You are a Radio DJ. The listener just tuned into the station "${station.name}" located in "${station.country}".
      The genre/tags are: "${station.tags.join(", ")}".
      
      Write a VERY short (maximum 10 words), ${tone} intro for this station.
      
      CRITICAL: 
      - Vary your phrasing. Do NOT always say "You are listening to". 
      - Sometimes use "Welcome to...", "Tuning into...", "Here is...", "Straight from [Country]...", or just the name with hype.
      - Be creative.
    `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: prompt,
      config: {
        temperature: 1.2,
      },
    });
    return response.text || `Tuning into ${station.name}.`;
  } catch (_error) {
    return `You are listening to ${station.name}.`;
  }
};
